import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Store {
  id: string;
  name: string;
  address: string;
  phone: string;
  description: string;
  category_id: string;
  image_url?: string;
}

interface Category {
  id: string;
  name: string;
  display_order: number;
}

interface StoreSelectionProps {
  onReservationSubmit: (storeId: string, storeName: string, date: string, time: string) => void;
}

export default function StoreSelection({ onReservationSubmit }: StoreSelectionProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [imagesLoaded, setImagesLoaded] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCategoriesAndStores();
  }, []);

  const fetchCategoriesAndStores = async () => {
    try {
      setLoading(true);
      setError('');

      // 병렬로 데이터 가져오기
      const [categoriesResult, storesResult] = await Promise.all([
        supabase.from('store_categories').select('*').order('display_order'),
        supabase.from('stores').select('*').order('display_order')
      ]);

      if (categoriesResult.error) throw categoriesResult.error;
      if (storesResult.error) throw storesResult.error;

      setCategories(categoriesResult.data || []);
      setStores(storesResult.data || []);

      // 이미지 프리로드
      if (storesResult.data) {
        storesResult.data.forEach(store => {
          const img = new Image();
          img.src = getImageForStore(store);
        });
      }
    } catch (error: any) {
      console.error('데이터 로딩 실패:', error);
      setError(error.message || '데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreClick = (storeId: string, storeName: string) => {
    const today = new Date().toISOString().split('T')[0];
    onReservationSubmit(storeId, storeName, today, '10:00');
  };

  const filteredStores = selectedCategory === 'all' 
    ? stores 
    : stores.filter(store => store.category_id === selectedCategory);

  const getImageForStore = (store: Store) => {
    // 데이터베이스에 이미지가 있으면 그것을 사용, 없으면 기본 이미지
    if (store.image_url) {
      return store.image_url;
    }

    // 기본 이미지 (데이터베이스에 이미지가 없을 때만 사용)
    const storeImageMap: Record<string, string> = {
      '강남 VIP룸': 'https://public.readdy.ai/ai/img_res/fa4e65927e35b447a41c5c69597f1ab1.jpg',
      '홍대 프리미엄룸': 'https://readdy.ai/api/search-image?query=Hongdae%20premium%20karaoke%20room%20with%20trendy%20modern%20design%2C%20colorful%20LED%20lights%20and%20stylish%20decor%2C%20youthful%20vibrant%20atmosphere&width=600&height=400&seq=hongdae-premium&orientation=landscape',
      '이태원 럭셔리룸': 'https://readdy.ai/api/search-image?query=Itaewon%20luxury%20karaoke%20room%20with%20high-end%20interior%20design%2C%20crystal%20chandeliers%20and%20plush%20seating%2C%20exclusive%20elegant%20ambiance&width=600&height=400&seq=itaewon-luxury&orientation=landscape',
      '압구정 스위트룸': 'https://readdy.ai/api/search-image?query=Apgujeong%20suite%20karaoke%20room%20with%20sophisticated%20upscale%20design%2C%20premium%20leather%20sofas%20and%20ambient%20lighting%2C%20refined%20luxurious%20space&width=600&height=400&seq=apgujeong-suite&orientation=landscape',
      '신촌 파티룸': 'https://readdy.ai/api/search-image?query=Sinchon%20party%20karaoke%20room%20with%20dynamic%20colorful%20lighting%2C%20disco%20ball%20and%20energetic%20atmosphere%2C%20fun%20entertainment%20space%20for%20groups&width=600&height=400&seq=sinchon-party&orientation=landscape',
      '건대 뮤직룸': 'https://readdy.ai/api/search-image?query=Konkuk%20University%20music%20karaoke%20room%20with%20professional%20sound%20system%2C%20musical%20instruments%20and%20acoustic%20panels%2C%20creative%20artistic%20vibe&width=600&height=400&seq=konkuk-music&orientation=landscape',
      '잠실 엔터테인먼트룸': 'https://readdy.ai/api/search-image?query=Jamsil%20entertainment%20karaoke%20room%20with%20large%20screen%20display%2C%20modern%20audio%20equipment%20and%20comfortable%20seating%2C%20spacious%20contemporary%20design&width=600&height=400&seq=jamsil-entertainment&orientation=landscape',
      '여의도 비즈니스룸': 'https://readdy.ai/api/search-image?query=Yeouido%20business%20karaoke%20room%20with%20professional%20corporate%20design%2C%20sleek%20minimalist%20interior%20and%20executive%20seating%2C%20formal%20elegant%20atmosphere&width=600&height=400&seq=yeouido-business&orientation=landscape',
      '송파 프라이빗룸': 'https://readdy.ai/api/search-image?query=Songpa%20private%20karaoke%20room%20with%20intimate%20cozy%20design%2C%20soft%20ambient%20lighting%20and%20comfortable%20furniture%2C%20exclusive%20personal%20space&width=600&height=400&seq=songpa-private&orientation=landscape',
      '마포 스페셜룸': 'https://readdy.ai/api/search-image?query=Mapo%20special%20karaoke%20room%20with%20unique%20creative%20design%2C%20artistic%20decor%20and%20premium%20amenities%2C%20distinctive%20memorable%20atmosphere&width=600&height=400&seq=mapo-special&orientation=landscape',
    };

    return storeImageMap[store.name] || 'https://readdy.ai/api/search-image?query=luxury%20karaoke%20room%20with%20modern%20elegant%20interior%20design&width=600&height=400&seq=default-store&orientation=landscape';
  };

  const handleImageLoad = (storeId: string) => {
    setImagesLoaded(prev => new Set(prev).add(storeId));
  };

  if (loading) {
    return (
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
              매장 선택
            </h2>
            <p className="text-lg text-white/80 font-semibold">
              원하시는 카테고리와 매장을 선택해주세요
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl bg-white/10 border border-white/20 animate-pulse">
                <div className="w-full h-64 bg-white/20"></div>
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-white/20 rounded w-3/4"></div>
                  <div className="h-4 bg-white/20 rounded w-full"></div>
                  <div className="h-4 bg-white/20 rounded w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="text-lg text-red-400 mb-4">{error}</div>
          <button 
            onClick={fetchCategoriesAndStores}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer font-extrabold"
          >
            다시 시도
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="reservation-section" className="py-20 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
            매장 선택
          </h2>
          <p className="text-lg text-white/80 font-semibold">
            원하시는 카테고리와 매장을 선택해주세요
          </p>
        </div>

        {/* 카테고리 탭 */}
        {categories.length > 0 && (
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white/10 backdrop-blur-xl rounded-full p-1 shadow-2xl border border-white/20">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-8 py-3 rounded-full font-extrabold transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white shadow-lg'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                전체
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-8 py-3 rounded-full font-extrabold transition-all whitespace-nowrap cursor-pointer ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 매장 그리드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredStores.map((store) => {
            const imageLoaded = imagesLoaded.has(store.id);
            return (
              <div 
                key={store.id}
                className="backdrop-blur-xl rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group bg-white/10 border border-white/20"
                onClick={() => handleStoreClick(store.id, store.name)}
              >
                <div className="relative w-full h-64 overflow-hidden bg-white/5">
                  {!imageLoaded && (
                    <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                  )}
                  <img 
                    src={getImageForStore(store)}
                    alt={store.name}
                    loading="lazy"
                    onLoad={() => handleImageLoad(store.id)}
                    className={`w-full h-full object-cover object-top group-hover:scale-110 transition-all duration-500 ${
                      imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-2xl font-extrabold text-white mb-1">{store.name}</h3>
                  </div>
                </div>
                
                <div className="p-6">
                  <p className="text-sm text-white/80 mb-4 font-medium">{store.description}</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-map-pin-line text-cyan-400 text-lg"></i>
                      </div>
                      <p className="text-sm text-white/90 flex-1 font-medium">{store.address}</p>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-phone-line text-cyan-400 text-lg"></i>
                      </div>
                      <p className="text-sm text-white/90 font-medium">{store.phone}</p>
                    </div>
                  </div>

                  <button 
                    className="w-full mt-6 bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 hover:shadow-lg text-white py-3 rounded-lg font-extrabold transition-all whitespace-nowrap cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleStoreClick(store.id, store.name);
                    }}
                  >
                    이 매장 예약하기
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {filteredStores.length === 0 && categories.length > 0 && (
          <div className="text-center py-12">
            <p className="text-white/70 font-semibold">해당 카테고리에 매장이 없습니다.</p>
          </div>
        )}

        {categories.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/70 font-semibold">카테고리 정보를 불러올 수 없습니다.</p>
          </div>
        )}
      </div>
    </section>
  );
}